import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Package, Loader2, X, ExternalLink, Store, Filter, Trash2, Check, ChevronLeft, ChevronRight, ZoomIn, Download, RefreshCw, Clock, Image as ImageIcon2 } from 'lucide-react';
import { Input, Button } from '../components/ui';
import { useShops } from '../contexts/ShopContext';
import { useAuth } from '../contexts/AuthContext';
import { deleteShopifyImage, ShopifyProduct, getCachedProducts, syncShopProducts } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './ProductsPage.css';

type StatusFilter = 'all' | 'active' | 'draft';

export function ProductsPage() {
    const navigate = useNavigate();
    const { shops, selectedShop, selectShop } = useShops();
    const { userProfile } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [products, setProducts] = useState<ShopifyProduct[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [hasImageFilter, setHasImageFilter] = useState(false);
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Load filter preferences from Firestore on mount
    useEffect(() => {
        const loadPreferences = async () => {
            if (!userProfile?.uid) return;

            try {
                const prefsDoc = await getDoc(doc(db, 'userPreferences', userProfile.uid));
                if (prefsDoc.exists()) {
                    const prefs = prefsDoc.data();
                    if (prefs.productsPage) {
                        if (prefs.productsPage.statusFilter) {
                            setStatusFilter(prefs.productsPage.statusFilter);
                        }
                        if (prefs.productsPage.searchQuery) {
                            setSearchQuery(prefs.productsPage.searchQuery);
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading filter preferences:', err);
            } finally {
                setPreferencesLoaded(true);
            }
        };

        loadPreferences();
    }, [userProfile?.uid]);

    // Save filter preferences to Firestore when they change (debounced)
    useEffect(() => {
        if (!userProfile?.uid || !preferencesLoaded) return;

        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounce save by 500ms
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await setDoc(doc(db, 'userPreferences', userProfile.uid), {
                    productsPage: {
                        statusFilter,
                        searchQuery,
                        updatedAt: new Date().toISOString()
                    }
                }, { merge: true });
            } catch (err) {
                console.error('Error saving filter preferences:', err);
            }
        }, 500);

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [statusFilter, searchQuery, userProfile?.uid, preferencesLoaded]);

    // Product detail modal
    const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null);

    // Image deletion state
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null); // "productId-imageId"
    const [deletingImageId, setDeletingImageId] = useState<string | null>(null);

    // Image lightbox state
    const [lightboxImages, setLightboxImages] = useState<{ src: string, alt: string, imageId: number }[]>([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [lightboxProductId, setLightboxProductId] = useState<number | null>(null);
    const [lightboxConfirmDelete, setLightboxConfirmDelete] = useState(false);
    const [lightboxDeleting, setLightboxDeleting] = useState(false);

    // Sync state
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

    // Real-time Firestore listener for products
    useEffect(() => {
        if (!selectedShop) {
            setProducts([]);
            return;
        }

        // Initial load from cache
        loadFromCache();

        // Set up real-time listener
        const productsRef = collection(db, 'products');
        const q = query(
            productsRef,
            where('shopId', '==', selectedShop.id),
            orderBy('shopifyCreatedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const updatedProducts: ShopifyProduct[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: data.productId,
                    title: data.title,
                    handle: data.handle,
                    vendor: data.vendor,
                    product_type: data.product_type,
                    status: data.status,
                    tags: data.tags,
                    images: data.images || [],
                    variants: data.variants || [],
                };
            });
            setProducts(updatedProducts);
            setIsLoading(false);
        }, (err) => {
            console.error('Firestore listener error:', err);
            // Fallback to API call if listener fails
            loadFromCache();
        });

        return () => unsubscribe();
    }, [selectedShop]);

    const loadFromCache = async () => {
        if (!selectedShop) return;

        setIsLoading(true);
        setError('');

        try {
            const result = await getCachedProducts(selectedShop.id);
            setProducts(result.products);
            setLastSyncedAt(result.lastSyncedAt);
        } catch (err: any) {
            console.error('Error loading products:', err);
            // If cache is empty, prompt for sync
            if (err.message?.includes('not found') || products.length === 0) {
                setError('Aucun produit en cache. Cliquez sur "Synchroniser" pour charger les produits depuis Shopify.');
            } else {
                setError(err.message || 'Erreur lors du chargement des produits');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSync = async () => {
        if (!selectedShop || isSyncing) return;

        setIsSyncing(true);
        setError('');

        try {
            const result = await syncShopProducts(selectedShop.id);
            setLastSyncedAt(new Date().toISOString());
            console.log(`Sync complete: ${result.syncedCount} products synced, ${result.deletedCount} deleted`);
        } catch (err: any) {
            console.error('Sync error:', err);
            setError(err.message || 'Erreur lors de la synchronisation');
        } finally {
            setIsSyncing(false);
        }
    };

    // Format last sync time
    const formatLastSync = (isoDate: string | null): string => {
        if (!isoDate) return 'Jamais';
        const date = new Date(isoDate);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'À l\'instant';
        if (diffMins < 60) return `Il y a ${diffMins} min`;
        if (diffHours < 24) return `Il y a ${diffHours}h`;
        return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    };


    // Get SKU from first variant
    const getProductSku = (product: ShopifyProduct): string => {
        if (product.variants && product.variants.length > 0 && product.variants[0].sku) {
            return product.variants[0].sku;
        }
        return '-';
    };

    // Lightbox handlers
    const openLightbox = (productId: number, images: { src: string, alt: string, imageId: number }[], startIndex: number, e: React.MouseEvent) => {
        e.stopPropagation();
        setLightboxProductId(productId);
        setLightboxImages(images);
        setLightboxIndex(startIndex);
    };

    const closeLightbox = () => {
        setLightboxImages([]);
        setLightboxIndex(0);
        setLightboxProductId(null);
        setLightboxConfirmDelete(false);
        setLightboxDeleting(false);
    };

    const nextImage = useCallback(() => {
        setLightboxIndex(prev => (prev + 1) % lightboxImages.length);
    }, [lightboxImages.length]);

    const prevImage = useCallback(() => {
        setLightboxIndex(prev => (prev - 1 + lightboxImages.length) % lightboxImages.length);
    }, [lightboxImages.length]);

    // Keyboard navigation for lightbox
    useEffect(() => {
        if (lightboxImages.length === 0) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowRight') nextImage();
            if (e.key === 'ArrowLeft') prevImage();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lightboxImages.length, nextImage, prevImage]);

    // Handle image download
    const handleDownloadImage = async (imageUrl: string, imageName: string) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${imageName}.jpg`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading image:', error);
        }
    };

    // Handle lightbox delete with confirmation
    const handleLightboxDelete = async () => {
        if (!lightboxProductId || lightboxImages.length === 0) return;

        // First click: show confirmation
        if (!lightboxConfirmDelete) {
            setLightboxConfirmDelete(true);
            // Auto-hide confirmation after 3 seconds
            setTimeout(() => {
                setLightboxConfirmDelete(false);
            }, 3000);
            return;
        }

        // Second click: proceed with deletion
        setLightboxDeleting(true);
        try {
            const currentImage = lightboxImages[lightboxIndex];

            // Call the API directly instead of handleDeleteImage to avoid confirmation loop
            if (selectedShop) {
                await deleteShopifyImage({
                    shopId: selectedShop.id,
                    productId: String(lightboxProductId),
                    imageId: String(currentImage.imageId),
                });

                // Update products list
                setProducts(prev => prev.map(p => {
                    if (p.id === lightboxProductId) {
                        return {
                            ...p,
                            images: p.images.filter(img => img.id !== currentImage.imageId),
                        };
                    }
                    return p;
                }));

                // Update selectedProduct if it's the same
                if (selectedProduct?.id === lightboxProductId) {
                    setSelectedProduct(prev => prev ? {
                        ...prev,
                        images: prev.images.filter(img => img.id !== currentImage.imageId),
                    } : null);
                }
            }

            // Update lightbox images
            const newImages = lightboxImages.filter((_, i) => i !== lightboxIndex);
            if (newImages.length === 0) {
                closeLightbox();
            } else {
                setLightboxImages(newImages);
                setLightboxIndex(prev => Math.min(prev, newImages.length - 1));
            }
        } catch (error: any) {
            console.error('Error deleting image:', error);
            setError(error.message || 'Erreur lors de la suppression');
        } finally {
            setLightboxDeleting(false);
            setLightboxConfirmDelete(false);
        }
    };

    // Handle image deletion
    const handleDeleteImage = async (productId: number, imageId: number) => {
        const deleteKey = `${productId}-${imageId}`;

        // If not in confirm mode, show confirmation
        if (confirmDeleteId !== deleteKey) {
            setConfirmDeleteId(deleteKey);
            // Auto-hide confirmation after 3 seconds
            setTimeout(() => {
                setConfirmDeleteId(prev => prev === deleteKey ? null : prev);
            }, 3000);
            return;
        }

        // Confirmed, proceed with deletion
        if (!selectedShop) return;

        setDeletingImageId(deleteKey);
        try {
            await deleteShopifyImage({
                shopId: selectedShop.id,
                productId: String(productId),
                imageId: String(imageId),
            });

            // Update local state - remove the image from the product
            setProducts(prev => prev.map(p => {
                if (p.id === productId) {
                    return {
                        ...p,
                        images: p.images.filter(img => img.id !== imageId),
                    };
                }
                return p;
            }));

            // Also update selectedProduct if it's the same
            if (selectedProduct?.id === productId) {
                setSelectedProduct(prev => prev ? {
                    ...prev,
                    images: prev.images.filter(img => img.id !== imageId),
                } : null);
            }

            setConfirmDeleteId(null);
        } catch (err: any) {
            console.error('Error deleting image:', err);
            setError(err.message || 'Erreur lors de la suppression de l\'image');
        } finally {
            setDeletingImageId(null);
        }
    };

    // Filter products by search, status, and has-image toggle
    const filteredProducts = products.filter(product => {
        // Has-image filter (AND condition) - only show products WITH images
        if (hasImageFilter) {
            if (!product.images || product.images.length === 0) {
                return false;
            }
        }

        // Status filter
        if (statusFilter !== 'all' && product.status?.toLowerCase() !== statusFilter) {
            return false;
        }

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const sku = getProductSku(product).toLowerCase();
            return (
                product.title.toLowerCase().includes(query) ||
                product.vendor?.toLowerCase().includes(query) ||
                product.product_type?.toLowerCase().includes(query) ||
                sku.includes(query)
            );
        }

        return true;
    });

    // Count by status
    const activeCount = products.filter(p => p.status?.toLowerCase() === 'active').length;
    const draftCount = products.filter(p => p.status?.toLowerCase() === 'draft').length;
    const hasImageCount = products.filter(p => p.images && p.images.length > 0).length;

    return (
        <div className="products-page">
            <header className="page-header">
                <div className="header-content">
                    <h1>
                        <Package className="header-icon" />
                        Produits Shopify
                    </h1>
                    <p>Parcourir et gérer vos produits</p>
                </div>
            </header>

            {/* Shop selector */}
            {shops.length > 0 && (
                <div className="shop-selector glass">
                    <label>Boutique :</label>
                    <select
                        value={selectedShop?.id || ''}
                        onChange={(e) => {
                            const shop = shops.find(s => s.id === e.target.value);
                            if (shop) selectShop(shop);
                        }}
                    >
                        <option value="">Sélectionner une boutique</option>
                        {shops.map((shop) => (
                            <option key={shop.id} value={shop.id}>
                                {shop.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Toolbar with filters */}
            {selectedShop && (
                <div className="products-toolbar glass">
                    <Input
                        placeholder="Rechercher par titre, SKU, vendeur..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        leftIcon={<Search size={18} />}
                        className="search-input"
                    />
                    <div className="status-filters">
                        <button
                            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('all')}
                        >
                            <Filter size={14} />
                            Tous ({products.length})
                        </button>
                        <button
                            className={`filter-btn ${statusFilter === 'active' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('active')}
                        >
                            Actifs ({activeCount})
                        </button>
                        <button
                            className={`filter-btn ${statusFilter === 'draft' ? 'active' : ''}`}
                            onClick={() => setStatusFilter('draft')}
                        >
                            Brouillons ({draftCount})
                        </button>
                        <button
                            className={`filter-btn toggle ${hasImageFilter ? 'active' : ''}`}
                            onClick={() => setHasImageFilter(!hasImageFilter)}
                        >
                            <ImageIcon2 size={14} />
                            Avec image ({hasImageCount})
                        </button>
                    </div>
                    <div className="products-count">
                        {filteredProducts.length} produit{filteredProducts.length !== 1 ? 's' : ''} affiché{filteredProducts.length !== 1 ? 's' : ''}
                    </div>

                    {/* Sync section */}
                    <div className="sync-section">
                        <span className="last-sync">
                            <Clock size={14} />
                            {formatLastSync(lastSyncedAt)}
                        </span>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleSync}
                            disabled={isSyncing}
                            leftIcon={isSyncing ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                        >
                            {isSyncing ? 'Synchronisation...' : 'Synchroniser'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Content */}
            {
                !selectedShop ? (
                    <div className="empty-state glass">
                        <Store size={64} className="empty-icon" />
                        <h2>Aucune boutique sélectionnée</h2>
                        <p>Sélectionnez une boutique Shopify pour voir ses produits.</p>
                        {shops.length === 0 && (
                            <Button onClick={() => navigate('/admin/shops')}>
                                Ajouter une boutique
                            </Button>
                        )}
                    </div>
                ) : isLoading ? (
                    <div className="loading-state glass">
                        <Loader2 className="spinner" size={48} />
                        <p>Chargement de tous les produits...</p>
                    </div>
                ) : error ? (
                    <div className="error-state glass">
                        <p className="error-message">{error}</p>
                        <div className="error-actions">
                            <Button onClick={loadFromCache}>Réessayer</Button>
                            <Button variant="primary" onClick={handleSync} disabled={isSyncing}>
                                {isSyncing ? 'Synchronisation...' : 'Synchroniser depuis Shopify'}
                            </Button>
                        </div>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="empty-state glass">
                        <Package size={64} className="empty-icon" />
                        <h2>Aucun produit trouvé</h2>
                        <p>
                            {searchQuery || statusFilter !== 'all'
                                ? 'Aucun produit ne correspond à vos critères.'
                                : 'Cette boutique ne contient pas encore de produits.'}
                        </p>
                    </div>
                ) : (
                    <div className="products-table-container glass">
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th className="col-images">Images</th>
                                    <th className="col-sku">SKU</th>
                                    <th className="col-title">Titre</th>
                                    <th className="col-vendor">Vendeur</th>
                                    <th className="col-status">Statut</th>
                                    <th className="col-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map((product) => (
                                    <tr
                                        key={product.id}
                                        onClick={() => setSelectedProduct(product)}
                                    >
                                        <td className="col-images">
                                            <div className="images-row">
                                                {product.images && product.images.length > 0 ? (
                                                    [...product.images]
                                                        .sort((a, b) => (a.position || 0) - (b.position || 0))
                                                        .map((image, index) => {
                                                            const deleteKey = `${product.id}-${image.id}`;
                                                            const isConfirming = confirmDeleteId === deleteKey;
                                                            const isDeleting = deletingImageId === deleteKey;

                                                            return (
                                                                <div key={image.id || index} className="image-wrapper">
                                                                    <img
                                                                        src={image.src}
                                                                        alt={image.alt || `${product.title} - image ${index + 1}`}
                                                                        className="product-thumbnail clickable"
                                                                        title={`Cliquer pour agrandir`}
                                                                        onClick={(e) => openLightbox(
                                                                            product.id,
                                                                            product.images.map(img => ({ src: img.src, alt: img.alt || product.title, imageId: img.id })),
                                                                            index,
                                                                            e
                                                                        )}
                                                                    />
                                                                    <div className="zoom-indicator">
                                                                        <ZoomIn size={12} />
                                                                    </div>
                                                                    {/* Show delete button only for images after the first one */}
                                                                    {index > 0 && (
                                                                        <button
                                                                            className={`image-delete-btn ${isConfirming ? 'confirming' : ''}`}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteImage(product.id, image.id);
                                                                            }}
                                                                            disabled={isDeleting}
                                                                            title={isConfirming ? 'Cliquez pour confirmer' : 'Supprimer cette image'}
                                                                        >
                                                                            {isDeleting ? (
                                                                                <Loader2 size={12} className="spin" />
                                                                            ) : isConfirming ? (
                                                                                <Check size={12} />
                                                                            ) : (
                                                                                <Trash2 size={12} />
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })
                                                ) : (
                                                    <div className="no-image">
                                                        <Package size={20} />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="col-sku">
                                            <span className="sku-text">{getProductSku(product)}</span>
                                        </td>
                                        <td className="col-title">
                                            <span className="product-title">{product.title}</span>
                                        </td>
                                        <td className="col-vendor">
                                            <span className="vendor-text">{product.vendor || '-'}</span>
                                        </td>
                                        <td className="col-status">
                                            <span className={`status-badge status-${product.status?.toLowerCase() || 'draft'}`}>
                                                {product.status === 'active' ? 'Actif' : 'Brouillon'}
                                            </span>
                                        </td>
                                        <td className="col-actions">
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    console.log('[Products] Navigating to generator with productId:', product.id);
                                                    navigate(`/apps/image-generator/generator?productId=${product.id}`);
                                                }}
                                            >
                                                Générer
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            }

            {/* Product Detail Modal */}
            {
                selectedProduct && (
                    <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
                        <div className="modal-content glass" onClick={(e) => e.stopPropagation()}>
                            <button className="modal-close" onClick={() => setSelectedProduct(null)}>
                                <X size={24} />
                            </button>

                            <div className="modal-header">
                                <div className="modal-images">
                                    {selectedProduct.images && selectedProduct.images.length > 0 ? (
                                        selectedProduct.images.map((image, index) => (
                                            <img
                                                key={image.id || index}
                                                src={image.src}
                                                alt={image.alt || `${selectedProduct.title} - image ${index + 1}`}
                                                className="modal-image clickable"
                                                title="Cliquer pour agrandir"
                                                onClick={(e) => openLightbox(
                                                    selectedProduct.id,
                                                    selectedProduct.images.map(img => ({ src: img.src, alt: img.alt || selectedProduct.title, imageId: img.id })),
                                                    index,
                                                    e
                                                )}
                                            />
                                        ))
                                    ) : (
                                        <div className="modal-no-image">
                                            <Package size={48} />
                                        </div>
                                    )}
                                </div>
                                <div className="modal-title-section">
                                    <h2>{selectedProduct.title}</h2>
                                    <span className={`status-badge status-${selectedProduct.status?.toLowerCase() || 'draft'}`}>
                                        {selectedProduct.status === 'active' ? 'Actif' : 'Brouillon'}
                                    </span>
                                </div>
                            </div>

                            <div className="modal-details">
                                <div className="detail-row">
                                    <span className="detail-label">SKU</span>
                                    <span className="detail-value">{getProductSku(selectedProduct)}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Vendeur</span>
                                    <span className="detail-value">{selectedProduct.vendor || '-'}</span>
                                </div>
                                <div className="detail-row">
                                    <span className="detail-label">Type</span>
                                    <span className="detail-value">{selectedProduct.product_type || '-'}</span>
                                </div>
                                {selectedProduct.variants && selectedProduct.variants.length > 0 && (
                                    <div className="detail-row">
                                        <span className="detail-label">Prix</span>
                                        <span className="detail-value">{selectedProduct.variants[0].price} €</span>
                                    </div>
                                )}
                            </div>

                            <div className="modal-actions">
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        setSelectedProduct(null);
                                        navigate(`/apps/image-generator/generator?productId=${selectedProduct.id}`);
                                    }}
                                >
                                    Générer une image
                                </Button>
                                <Button
                                    variant="secondary"
                                    leftIcon={<ExternalLink size={16} />}
                                    onClick={() => {
                                        const domain = selectedShop?.domain?.replace('.myshopify.com', '');
                                        window.open(`https://admin.shopify.com/store/${domain}/products/${selectedProduct.id}`, '_blank');
                                    }}
                                >
                                    Voir sur Shopify
                                </Button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Image Lightbox */}
            {
                lightboxImages.length > 0 && (
                    <div className="lightbox-overlay" onClick={closeLightbox}>
                        <button className="lightbox-close" onClick={closeLightbox}>
                            <X size={28} />
                        </button>

                        {lightboxImages.length > 1 && (
                            <>
                                <button
                                    className="lightbox-nav lightbox-prev"
                                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                                >
                                    <ChevronLeft size={36} />
                                </button>
                                <button
                                    className="lightbox-nav lightbox-next"
                                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                                >
                                    <ChevronRight size={36} />
                                </button>
                            </>
                        )}

                        <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                            <img
                                src={lightboxImages[lightboxIndex].src}
                                alt={lightboxImages[lightboxIndex].alt}
                                className="lightbox-image"
                            />
                        </div>

                        {lightboxImages.length > 1 && (
                            <div className="lightbox-counter">
                                {lightboxIndex + 1} / {lightboxImages.length}
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="lightbox-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                                className="lightbox-action-btn"
                                onClick={() => handleDownloadImage(
                                    lightboxImages[lightboxIndex].src,
                                    lightboxImages[lightboxIndex].alt || 'image'
                                )}
                                title="Télécharger l'image"
                            >
                                <Download size={20} />
                            </button>
                            {lightboxIndex > 0 && (
                                <button
                                    className={`lightbox-action-btn lightbox-delete-btn ${lightboxConfirmDelete ? 'confirming' : ''}`}
                                    onClick={handleLightboxDelete}
                                    disabled={lightboxDeleting}
                                    title={lightboxConfirmDelete ? 'Cliquez pour confirmer la suppression' : 'Supprimer l\'image'}
                                >
                                    {lightboxDeleting ? (
                                        <Loader2 size={20} className="spin" />
                                    ) : lightboxConfirmDelete ? (
                                        <Check size={20} />
                                    ) : (
                                        <Trash2 size={20} />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                )
            }
        </div >
    );
}
